// Voice messages (plan v2 §5.3): what an IVR call plays before the person presses a digit. An uploaded recording
// (WAV 8 kHz mono or MP3, at most 5 MB) or words spoken by text to speech. A message never changes: new words or a
// new file = a new version. A second person approves it; on approval its words are registered in the CMP as consent
// wording (the digit press is our consent, N1), and only then can an IVR node play it.
import { z } from 'zod'
import { Id, IsoDateTime } from './common'

export const VoiceLanguage = z.enum(['en', 'hi', 'ta', 'te'])
export type VoiceLanguage = z.infer<typeof VoiceLanguage>

export const VoiceSource = z.enum(['recording', 'tts'])
export type VoiceSource = z.infer<typeof VoiceSource>

export const VoiceStatus = z.enum(['waiting_approval', 'approved', 'rejected'])
export type VoiceStatus = z.infer<typeof VoiceStatus>

export const AudioFormat = z.enum(['wav', 'mp3'])
export type AudioFormat = z.infer<typeof AudioFormat>

export const MAX_AUDIO_BYTES = 5 * 1024 * 1024
export const MAX_AUDIO_SECONDS = 60
/** Exotel plays telephone sound: a WAV must already be 8 kHz mono, so nothing is lost or changed on the way. */
export const WAV_SAMPLE_RATE = 8000

/** What the portal keeps of an uploaded file (the file itself goes to our storage; Exotel gets a link). */
export const AudioFile = z.object({
  name: z.string(),
  format: AudioFormat,
  size_bytes: z.number().int().min(1),
  seconds: z.number().min(0),
})
export type AudioFile = z.infer<typeof AudioFile>

export const VoiceMessage = z.object({
  id: Id,
  name: z.string().describe('the same name for every version, e.g. "Consent ask"'),
  version: z.number().int().min(1),
  source: VoiceSource,
  language: VoiceLanguage,
  words: z
    .string()
    .describe('text to speech: the words spoken; a recording: what it says (the consent wording)'),
  file: AudioFile.nullable().describe('a recording only'),
  seconds: z.number().min(0).describe('how long it plays (text to speech: worked out from the words)'),
  status: VoiceStatus,
  made_by: Id,
  made_by_name: z.string(),
  made_at: IsoDateTime,
  decided_by_name: z.string().nullable(),
  decided_at: IsoDateTime.nullable(),
  note: z.string().describe('why it was rejected; empty otherwise'),
  wording_version: z
    .string()
    .nullable()
    .describe('the CMP consent wording version it was registered as on approval, e.g. ivr-consent-ask-v1'),
  used_by: z.array(z.string()).describe('the workflow versions whose IVR node plays it'),
})
export type VoiceMessage = z.infer<typeof VoiceMessage>

export const VoiceWords = z
  .string()
  .trim()
  .min(20, 'Write what the message says (at least 20 letters)')
  .max(600)

/** The file as the browser read it: a WAV's header gives the sample rate and channels; an MP3 has none (null). */
export const AudioFileInput = AudioFile.extend({
  size_bytes: z.number().int().min(1).max(MAX_AUDIO_BYTES, 'At most 5 MB'),
  seconds: z
    .number()
    .min(1, 'At least 1 second')
    .max(MAX_AUDIO_SECONDS, `At most ${MAX_AUDIO_SECONDS} seconds`),
  sample_rate: z.number().int().nullable(),
  channels: z.number().int().nullable(),
}).refine((file) => file.format !== 'wav' || (file.sample_rate === WAV_SAMPLE_RATE && file.channels === 1), {
  message: 'A WAV must be 8 kHz mono (telephone sound)',
})
export type AudioFileInput = z.infer<typeof AudioFileInput>

const TtsContent = z.object({ source: z.literal('tts'), language: VoiceLanguage, words: VoiceWords })
const RecordingContent = z.object({
  source: z.literal('recording'),
  language: VoiceLanguage,
  words: VoiceWords.describe(
    'what the recording says, word for word: it is registered as the consent wording'
  ),
  file: AudioFileInput,
})

/** A new version of a message: new words or a new file. */
export const VoiceContentInput = z.discriminatedUnion('source', [TtsContent, RecordingContent])
export type VoiceContentInput = z.infer<typeof VoiceContentInput>

export const VoiceName = z.string().trim().min(3, 'Give it a name, e.g. Consent ask').max(60)

export const CreateVoiceMessageInput = z.discriminatedUnion('source', [
  TtsContent.extend({ name: VoiceName }),
  RecordingContent.extend({ name: VoiceName }),
])
export type CreateVoiceMessageInput = z.infer<typeof CreateVoiceMessageInput>

export const VoiceDecisionInput = z.object({
  decision: z.enum(['approve', 'reject']),
  note: z.string().trim().max(200).optional(),
})
export type VoiceDecisionInput = z.infer<typeof VoiceDecisionInput>

export interface VoiceMessagesApi {
  /** newest version first within each name */
  listVoiceMessages(): Promise<VoiceMessage[]>
  createVoiceMessage(input: CreateVoiceMessageInput): Promise<VoiceMessage>
  /** version n + 1 of the message `fromId` belongs to; it waits for approval like a new one */
  newVoiceVersion(fromId: string, input: VoiceContentInput): Promise<VoiceMessage>
  /** a second person only, never the maker; approve registers the words in the CMP */
  decideVoiceMessage(id: string, input: VoiceDecisionInput): Promise<VoiceMessage>
  /** C-ALL (backend only): the recording itself, uploaded right after the version is saved (the mock keeps none) */
  uploadVoiceFile?(id: string, file: Blob): Promise<void>
  /** C-ALL (backend only): where Play gets the sound; null = no sound (the mock) */
  voiceFileUrl?(message: VoiceMessage): string | null
}
